// src/pages/Login.js
import React, { useState } from 'react';
import userIcon from '../assets/user.png';
import lockedIcon from '../assets/locked.png';
import { Link, useNavigate } from 'react-router-dom';
import backgroundImage from '../assets/back.jpg';

// Mock user data for demonstration
const mockUsers = [
  { username: 'admin', password: 'Adminpassword@2023', role: 'admin', id: 'admin1' },
  { username: 'doctor', password: 'Doctorpassword@2023', role: 'doctor', id: 'doc1' },
  { username: 'patient', password: 'Patientpassword@2023', role: 'patient', id: 'pat001' },
];

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const logInAs = (user) => {
    localStorage.setItem('userRole', user.role);
    localStorage.setItem('userId', user.id);
    navigate('/home');
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    const foundUser = mockUsers.find(
      (user) => user.username === username && user.password === password
    );

    if (foundUser) {
      logInAs(foundUser);
    } else {
      setError('Invalid username or password.');
    }
  };

  // Quick demo login — signs in immediately without needing to type credentials
  const handleQuickLogin = (role) => {
    const user = mockUsers.find((u) => u.role === role);
    if (user) {
      setError('');
      logInAs(user);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Background photo, tied to the palette with a duotone overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center -z-10"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      <div
        className="absolute inset-0 -z-10"
        style={{ background: 'linear-gradient(160deg, rgba(22,38,43,0.85) 0%, rgba(14,124,123,0.55) 100%)' }}
      />

      <div className="flex flex-col-reverse md:flex-row items-center md:items-start gap-4 md:gap-6 w-full max-w-3xl">
        {/* Quick demo login — 3 independent stacked buttons, to the left of the form */}
        <div className="flex flex-col gap-3 w-full md:w-auto">
          <button
            type="button"
            onClick={() => handleQuickLogin('admin')}
            className="bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium py-2.5 px-6 rounded-full transition-colors duration-200 whitespace-nowrap"
          >
            Log in as Admin
          </button>
          <button
            type="button"
            onClick={() => handleQuickLogin('doctor')}
            className="bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium py-2.5 px-6 rounded-full transition-colors duration-200 whitespace-nowrap"
          >
            Log in as Doctor
          </button>
          <button
            type="button"
            onClick={() => handleQuickLogin('patient')}
            className="bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium py-2.5 px-6 rounded-full transition-colors duration-200 whitespace-nowrap"
          >
            Log in as Patient
          </button>
        </div>

        {/* Login form */}
        <div className="bg-white/95 backdrop-blur-sm border border-[var(--mist)] shadow-xl p-8 rounded-3xl w-full max-w-md mx-auto md:mx-0">
          <span
            className="block text-[var(--coral)] text-xs tracking-[0.2em] uppercase text-center mb-2"
            style={{ fontFamily: 'var(--font-mono)' }}
          >
            Welcome Back
          </span>
          <h2
            className="text-3xl font-medium text-center text-[var(--ink)] mb-6"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Log In
          </h2>

          <form id="form" onSubmit={handleLogin} autoComplete="off">
            <div className="mb-6">
              <label htmlFor="username" className="block text-[var(--ink)] text-sm font-medium mb-2">Username</label>
              <div className="relative">
                <img src={userIcon} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 opacity-60" alt="" />
                <input
                  id="username"
                  name="username"
                  type="text"
                  placeholder="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="w-full pl-11 pr-4 py-2.5 border border-[var(--mist)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--teal)] transition-shadow"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="password" className="block text-[var(--ink)] text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <img src={lockedIcon} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 opacity-60" alt="" />
                <input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="············"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}"
                  title="Must contain at least one number and one uppercase and lowercase letter, and at least 8 or more characters and one or more of these special characters: .@$!%*#?&><)(^-_"
                  required
                  className="w-full pl-11 pr-4 py-2.5 border border-[var(--mist)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--teal)] transition-shadow"
                />
              </div>
              <div className="text-right mt-2">
                <Link to="/forget-password" className="text-sm text-[var(--teal-deep)] hover:underline">
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && <p className="text-[var(--coral)] text-sm mb-4">{error}</p>}

            <button
              type="submit"
              className="w-full bg-[var(--teal)] hover:bg-[var(--teal-deep)] text-white font-medium py-2.5 px-4 rounded-full text-lg transition-colors duration-200"
            >
              Log in
            </button>

            <div className="text-center mt-4 text-[var(--ink)]/70 text-sm">
              Need an account?{' '}
              <Link to="/signup" className="text-[var(--teal-deep)] font-medium hover:underline">Sign up</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
